import { Injectable, Output, EventEmitter, Directive } from "@angular/core";
import {
  faFileAlt,
  faPlusCircle,
  faFlag,
} from "@fortawesome/free-solid-svg-icons";
import { Subject } from "rxjs";
import { AppService } from "../app.service";

@Directive()
@Injectable({
  providedIn: "root",
})
export class ReportingPagesService {
  @Output() onArchiveClicked = new EventEmitter();
  private archiveMethodSource = new Subject<any>();
  archiveMethod$ = this.archiveMethodSource.asObservable();
  legacyUrl = `${window.caUrl}/`;

  elasticSearchPages = ["allcompany", "models", "sector", "industry"];

  constructor(private appService: AppService) {}

  getViewsObj(row) {
    return {
      show: true,
      showAnalyst:
        row.show_sar &&
        (row.status === "Covered" || row.status === "In Progress"),
      analystLink: `/mex/${row.ticker}/SAR/${row.sar_tab}/${row.sar_source}`,
      analystIcon: row.show_as_reported,
      showDashboard: row.show_dashboard,
      companyLink: `/mex/${row.ticker}/NMV/${encodeURIComponent(row.nmv_tab)}`,
      dashboardLink: `/company/${row.ticker}/analystdashboard`,
    };
  }
  getFilterUrl(params) {
    let filterUrl = "";
    const isElastic = this.elasticSearchPages.includes(params.page);
    //internal filters

    let filterObj = {
      groupOp: "AND",
      rules: [...params.filters],
    };
    filterUrl = `&filters=${JSON.stringify(filterObj)}`;
    if (params.globalSearch && params.globalSearch !== "") {
      //global serach rule
      if (isElastic) {
        // make url based on elastic

        filterUrl += `&global_search=${params.globalSearch}`;
      } else {
        // make url as per old jqgrid format
        const cols = params.columns.map((val) => {
          return {
            field: val.field,
            op: "cn",
            data: encodeURIComponent(params.globalSearch),
          };
        });
        filterObj = {
          groupOp: "OR",
          rules: cols,
        };
        filterUrl = `&filters=${JSON.stringify(filterObj)}`;
      }
    }
    if (params.dataCovTypeId !== void 0) {
      filterUrl += `&dataCovTypeId=${params.dataCovTypeId}`;
    }
    if (params.watchlist_id !== void 0) {
      filterUrl += `&watchlist_id=${params.watchlist_id}`;
    }
    return filterUrl;
  }
  getAllBrokersData(data) {
    let formattedData = [];

    formattedData = data.rows.map((row) => {
      const disclosureArr = [];
      if (row.disclosure_list) {
        const clickCallback = this.disclosureClick;
        row.disclosure_list.forEach((element) => {
          let disclosureUrl = element.url ? element.url : "";
          if (!disclosureUrl.includes("http")) {
            //SELLSIDE-1666
            if (disclosureUrl.charAt(0) === "/") {
              disclosureUrl = disclosureUrl.slice(1);
            }
            disclosureUrl = this.legacyUrl + disclosureUrl;
          }
          disclosureArr.push({
            icon: faFileAlt,
            show: true,
            title: element ? element.name : "",
            clickData: {
              link: disclosureUrl,
            },
            clickCallback: clickCallback,
          });
        });
      }
      let vaconsensus = row.vaconsensus;
      if (row.vaconsensus === -1) {
        vaconsensus = "Not Entitled";
      } else if (row.vaconsensus === -2) {
        vaconsensus = "Not Participating";
      }
      let total = row.total;
      if (row.total === -1) {
        total = "Not Entitled";
      } else if (row.total === -2) {
        total = "Not Participating";
      }

      const rowObj = {
        name: {
          url:
            row.isentitled.toLowerCase() === "entitled"
              ? `/reporting/broker/${row.shortname}`
              : "",
          displayText: row.name,
          name: row.shortname,
        },
        total: total,
        vaconsensus: vaconsensus,
        isentitled: row.isentitled,
        disclosure: disclosureArr,
      };

      return rowObj;
    });
    return formattedData;
  }
  disclosureClick(data) {
    window.open(data.link);
  }
  getBrokerData(data) {
    let formattedData = [];

    formattedData = data.rows.map((row) => {
      const rowObj = {
        analyst: {
          url: `/reporting/analyst/${row.analystid}`,
          displayText: row.analyst,
          name: row.analyst,
          tooltipText: "View Analyst Page",
        },
        ticker: {
          url: `/company/${row.ticker}`,
          displayText: row.ticker,
          name: row.ticker,
          tooltipText: "View Company Page",
        },
        companyname: {
          url: `/company/${row.ticker}`,
          displayText: row.companyname,
          name: row.companyname,
          tooltipText: "View Company Page",
        },
        country: row.country,
        sectorname: {
          url: `/reporting/sector/${row.sectorname}`,
          displayText: row.sectorname,
          name: row.sectorname,
        },
        industryname: {
          url: `/reporting/industry/${row.sectorname}/${row.industryname}`,
          displayText: row.industryname,
          name: row.industryname,
        },
        modeldate: row.modeldate,
        peers: row.peers,
        filesize: {
          linkText: row.filesize,
          sourceStatus: row.source_status,
          entitled: row.entitled_to_model_download,
          downloadApplicable: row.download_applicable,
          clickCallback: this.modelDownloadClick.bind(this),
          clickData: {
            companyId: row.companyid,
            pg: row.nmv_tab,
            source:
              row.source_shortname !== undefined
                ? row.source_shortname
                : row.source,
            transactionId: row.txnid,
          },
        },
        inconsensus: row.inconsensus,
        coveragestatus: row.coveragestatus,
        views: this.getViewsObj(row),
      };

      return rowObj;
    });
    return formattedData;
  }
  modelDownloadClick(data) {
    const extraParams = {
      companyId: data.companyId,
      cut: "HOME",
      source: data.source,
      pg: data.pg,
      transactionId: data.transactionId,
    };

    this.appService.excelDownload("model_download", extraParams);
  }

  //Sector Page
  getSectorData(data) {
    let formattedData = [];

    formattedData = data.rows.map((row) => {
      const rowObj = {
        industryname: {
          url: `/comparative-analysis/current`,
          queryParams: {
            comp_type: "industry",
            id: row.industryid,
            sub_id: -1,
          },
          displayText: row.industryname,
          name: row.industryname,
        },
        ticker: {
          url: `/company/${row.ticker}`,
          displayText: row.ticker,
          name: row.ticker,
          tooltipText: "View Company Page",
        },
        companyname: {
          url: `/company/${row.ticker}`,
          displayText: row.companyname,
          name: row.companyname,
          tooltipText: "View Company Page",
        },
        coveragestatus: row.coveragestatus,
        // sectorname: {
        //   url: `/reporting/sector/${row.sectorname}`,
        //   displayText: row.sectorname,
        //   name: row.sectorname
        // },
        coveredsource: row.coveredsource,
        views: this.getViewsObj(row),
      };

      return rowObj;
    });
    return formattedData;
  }

  //Industry Page
  getIndustryData(data) {
    let formattedData = [];

    formattedData = data.rows.map((row) => {
      const rowObj = {
        ticker: {
          url: `/company/${row.ticker}`,
          displayText: row.ticker,
          name: row.ticker,
          tooltipText: "View Company Page",
        },
        companyname: {
          url: `/company/${row.ticker}`,
          displayText: row.companyname,
          name: row.companyname,
          tooltipText: "View Company Page",
        },
        coveragestatus: row.coveragestatus,
        sectorname: {
          url: `/reporting/sector/${row.sectorname}`,
          displayText: row.sectorname,
          name: row.sectorname,
        },
        coveredsource: row.coveredsource,
        views: this.getViewsObj(row),
      };

      return rowObj;
    });
    return formattedData;
  }

  //Analyst Page
  getAnalystData(data) {
    let formattedData = [];

    formattedData = data.rows.map((row) => {
      const rowObj = {
        ticker: {
          url: `/company/${row.ticker}`,
          displayText: row.ticker,
          target: "",
          name: row.ticker,
          tooltipText: "View Company Page",
        },
        companyname: {
          url: `/company/${row.ticker}`,
          displayText: row.companyname,
          target: "",
          name: row.companyname,
          tooltipText: "View Company Page",
        },
        country: row.country,
        sectorname: {
          url: `/reporting/sector/${row.sectorname}`,
          displayText: row.sectorname,
          target: "",
          name: row.sectorname,
        },
        industryname: {
          url: `/reporting/industry/${row.sectorname}/${row.industryname}`,
          displayText: row.industryname,
          name: row.industryname,
          target: "",
        },
        modeldate: row.modeldate,
        peers: row.peers,
        filesize: {
          linkText: row.filesize,
          sourceStatus: row.source_status,
          entitled: row.entitled_to_model_download,
          downloadApplicable: row.download_applicable,
          clickCallback: this.modelDownloadClick.bind(this),
          clickData: {
            companyId: row.companyid,
            pg: row.nmv_tab,
            source:
              row.source_shortname !== undefined
                ? row.source_shortname
                : row.source,
            transactionId: row.txnid,
          },
        },
        inconsensus: row.inconsensus,
        coveragestatus: row.coveragestatus,
        views: this.getViewsObj(row),
      };

      return rowObj;
    });
    return formattedData;
  }

  //AllCompany Page
  getAllCompanyData(data) {
    let formattedData = [];

    formattedData = data.rows.map((row) => {
      const rowObj = {
        companyname: {
          url: `/company/${row.ticker}`,
          displayText: row.companyname,
          name: row.companyname,
          tooltipText: "View Company Page",
        },
        ticker: {
          url: `/company/${row.ticker}`,
          displayText: row.ticker,
          name: row.ticker,
          tooltipText: "View Company Page",
        },
        bloombergcode: {
          url: `/company/${row.ticker}`,
          displayText: row.bloombergcode ? row.bloombergcode : "",
          name: row.bloombergcode,
          tooltipText: "View Company Page",
        },
        country: row.country,
        region: row.region,
        sectorname: {
          url: `/reporting/sector/${row.sectorname}`,
          displayText: row.sectorname,
          name: row.sectorname,
        },
        industryname: {
          url: `/reporting/industry/${row.sectorname}/${row.industryname}`,
          displayText: row.industryname,
          name: row.industryname,
        },
        coveragestatus: row.coveragestatus,
        coveredsource: row.coveredsource,
        companyinitiated: row.companyinitiated,
        lastreportingdate: row.lastreportingdate,
        views: this.getViewsObj(row),
      };

      return rowObj;
    });
    return formattedData;
  }

  //Model Data
  getAllModelData(data) {
    let formattedData = [];

    formattedData = data.rows.map((row) => {
      const rowObj = {
        modeldate: row.modeldate,
        ticker: {
          url: `/company/${row.ticker}`,
          displayText: row.ticker,
          name: row.ticker,
          tooltipText: "View Company Page",
        },
        bloombergcode: {
          url: `/company/${row.ticker}`,
          displayText: row.bloombergcode ? row.bloombergcode : "",
          name: row.bloombergcode,
          tooltipText: "View Company Page",
        },
        companyname: {
          url: `/company/${row.ticker}`,
          displayText: row.companyname,
          name: row.companyname,
          tooltipText: "View Company Page",
        },
        sectorname: {
          url: `/reporting/sector/${row.sectorname}`,
          displayText: row.sectorname,
          name: row.sectorname,
        },
        industryname: {
          url: `/reporting/industry/${row.sectorname}/${row.industryname}`,
          displayText: row.industryname,
          name: row.industryname,
        },
        source: {
          url: `/reporting/broker/${row.source_shortname}`,
          displayText: row.source,
          name: row.source_shortname,
        },
        waterfrontBadge: {
          icon: faFlag,
          show: row.wf_flag,
          title: row.wf_comment,
          clickCallback: null,
          iconColor: "green",
        },
        analyst: {
          url: `/reporting/analyst/${row.analystid}`,
          displayText: row.analyst,
          name: row.analyst,
          tooltipText: "View Analyst Page",
        },
        filesize: {
          linkText: row.filesize,
          sourceStatus: row.source_status,
          entitled: row.entitled_to_model_download,
          downloadApplicable: row.download_applicable,
          clickCallback: this.modelDownloadClick.bind(this),
          clickData: {
            companyId: row.companyid,
            pg: row.nmv_tab,
            source:
              row.source_shortname !== undefined
                ? row.source_shortname
                : row.source,
            transactionId: row.txnid,
          },
        },
        historicalModelLink: {
          icon: faPlusCircle,
          show: row.ticker !== undefined && row.sourceid !== undefined,
          title: "",
          clickCallback: this.archiveClick.bind(this),
          clickData: {
            sourceId: row.sourceid,
            ticker: row.ticker,
            companyId: row.companyid,
            pg: row.nmv_tab,
          },
        },
        coveragestatus: row.coveragestatus,
        views: this.getViewsObj(row),
      };
      return rowObj;
    });
    return formattedData;
  }

  archiveClick(data) {
    this.archiveMethodSource.next(data);
  }

  logUserOnRedirection(page, extra) {
    const extras = {
      UTCTIME: new Date().toUTCString(),
      URL: location.href,
      ...extra,
    };
    this.appService.logUserAction({}, "", "", page, extras, "", "");
  }
}
